import { url } from 'inspector';
import { CameraModel, IPropsCameraModel } from '@shared/models/camera.model';
import { IPropsSituationsModel, SituationsModel } from '@shared/models/situations.model';
import { Co2Model, IPropsCo2Model } from '@shared/models/co2.model';
import { LuminaryModel, IPropsLuminaryModel } from '@shared/models/luminary.model';
import { GarbageModel, IPropsGarbageModel } from '@shared/models/garbage.model';
import { IPropsTrafficModel, TrafficModel } from '@shared/models/traffic.model';
import { IPropsTrafficSpeedRangeModel, TrafficSpeedRangeModel } from '@shared/models/traffic-speed-range.model';
import { IPropsTrafficConcordanceModel, TrafficConcordanceModel } from '@shared/models/traffic-concordance.model';

import { BicyclePathModel, IPropsBicyclePathModel } from '@shared/models/bicycle-path.model';
import { IPropsParkingLotModel, ParkingLotModel } from '@shared/models/parking-lot.model';
import { IPropsTrafficLightModel, TrafficLightModel } from '@shared/models/traffic-light.model';
import { AforoModel, IPropsAforoModel } from '@shared/models/aforo.model';
import { AgentsModel, IPropsAgentsModel } from '@shared/models/agents.model';
import { Icon } from '@types-custom/models/ui/icon-model';
import { iconClassSituationsMap } from './geo-marker-map';
import { AgentsHistoricalModel, IPropsAgentsHistoricalModel } from '@shared/models/agents-historical.model';
import {
  IPropsTrafficManagmentPathModel,
  TrafficManagmentPlansPathModel
} from '@shared/models/trafficmanagmentplans-path.model';
import { IPropsStopBusModel, StopBusModel } from '@shared/models/stopbus.model';
import { IPropsZonesPathModel, ZonesPathModel } from '@shared/models/zones-path.model';
import { IPropsRoadRunPathModel, RoadRunPathModel } from '@shared/models/roadrun-path.model';
import { AccidentModel, IPropsAccidentModel } from '@shared/models/accident.model';
import { IPmvModel, PmvModel } from '@shared/models/pmv.model';
import { IPropsRuntDatexModel, RuntDatexModel } from '@shared/models/runt-datex.model';
import { IPropsSkateboardModel, SkateboardModel } from '@shared/models/skateboard.model';
import { IPropsRouteBusModel, RouteBusModel } from '@shared/models/routebus.model';
import {
  IPropsParkingSpotModel,
  IPropsUndergroundParkingSpotModel,
  ParkingSpotModel,
  UndergroundParkingSpotModel
} from '@shared/models/parking-spot.model';
import { IPropsPanicButtonModel, IPropsParkingLineModel, PanicButtonModel, ParkingLineModel, ParkingLinesEnum } from '@shared/models/parking-line.model';
import { AirQualityModel, IPropsAirQualityModel } from '@shared/models/air_quality/AirQuality.model';
import { IPropsParkingLoadingZoneModel, ParkingLoadingZoneModel } from '@shared/models/parking-loading-zone.model';
import { IPropsMatrixODModel, MatrixODModel } from '@shared/models/matrixOD.model';
import { IPropsMatrixCameraODModel, MatrixODCameraModel } from '@shared/models/matrixOD.Cameras.model';
import { CameraPointsModel, IPropsCameraPointModel } from '@shared/models/cameraPoint.model';

export const co2FromAPIMapper = (apiObj: any): Co2Model => {
  const propsCo2: IPropsCo2Model = {
    id: apiObj?.properties?.id,
    title: apiObj?.properties?.address,
    markerPopupText: apiObj?.properties?.address,
    location: apiObj?.sensors?.address,
    carbonDioxide: apiObj?.sensors?.co2,
    temperature: apiObj?.sensors?.temperature,
    humidity: apiObj?.sensors?.humidity,
    pressure: apiObj?.sensors?.pressure,
    icon: Icon.layer_co2,
  };

  const geometry = {
    coordinates: apiObj?.geometry?.coordinates,
    type: apiObj?.geometry?.type,
  };
  return new Co2Model(propsCo2, geometry);
};

export const luminaryFromAPIMapper = (apiObj: any): LuminaryModel => {
  const rawStatus = apiObj?.properties?.status;
  const assetIdValue = apiObj?.properties?.assetId || apiObj?.properties?.id;
  const assetId = assetIdValue?.toString() ?? '';
  // Simula estados con horario de Colombia y deja algunos como unknown
  const simulatedStatus =
    rawStatus === 'unknown'
      ? assetId
        ? (() => {
            const numericId = parseInt(assetId.replace(/\D/g, ''), 10) || 0;
            if (numericId % 5 === 0) {
              return 'unknown';
            }
            const hour = getColombiaHour();
            return hour >= 17 || hour < 6 ? 'Activo' : 'Inactivo';
          })()
        : 'Activo'
      : rawStatus;

  const propsLuminary: IPropsLuminaryModel = {
    id: assetIdValue,
    title: assetId || '',
    markerPopupText: assetId || '',
    assetId: assetIdValue,
    operator: apiObj?.properties?.operator,
    elementIdentification: apiObj?.properties?.elementIdentification,
    lampType: apiObj?.properties?.lampType,
    dimmingStatus: apiObj?.properties?.dimmingStatus,
    controlProgramName: apiObj?.properties?.controlProgramName,
    monitoringProgramName: apiObj?.properties?.monitoringProgramName,
    alarmProgramName: apiObj?.properties?.alarmProgramName,
    status: simulatedStatus,
    associatedTelecellId: apiObj?.properties?.associatedTelecellId,
    icon:
      simulatedStatus === 'unknown'
        ? Icon.layer_luminary_no_signal
        : simulatedStatus === 'Activo'
          ? Icon.layer_luminary_on
          : simulatedStatus === 'Inactivo'
            ? Icon.layer_luminary_off
            : Icon.layer_luminary_no_signal,
  };

  const geometry = {
    coordinates: apiObj?.geometry?.coordinates,
    type: apiObj?.geometry?.type,
  };
  return new LuminaryModel(propsLuminary, geometry);
};

const getColombiaHour = (): number => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Bogota',
    hour: '2-digit',
    hour12: false,
  }).formatToParts(new Date());
  const hour = parts.find((part) => part.type === 'hour')?.value ?? '0';
  return parseInt(hour, 10);
};

export const garbageFromAPIMapper = (apiObj: any): GarbageModel => {
  const props = apiObj?.properties ?? {};
  const title =
    props.binName || props.location ||
    `Bin ${props.binId || props.rfid} - ${props.binCategory || 'N/A'}`;
  
  const fillingLevel = parseFloat(props.binFillingLevel ?? '0');
  let garbageIcon = Icon.garbage_empty; 
  
  if (fillingLevel >= 70) {
    garbageIcon = Icon.garbage_full;
  } else if (fillingLevel >= 40) {
    garbageIcon = Icon.garbage_middle;
  }
  
  const propsGarbage: IPropsGarbageModel = {
    id: props.binId || props.rfid || props.id?.toString(),
    title: props.rfid ?? '',
    markerPopupText: title,
    rfid: props.rfid ?? '',
    binCapacity: props.binCapacity ?? '',
    binColor: props.binColor ?? '',
    binFullnessThreshold: props.binFullnessThreshold ?? 80,
    binId: props.binId ?? '',
    binLoggedTime: props.binLoggedTime ?? '',
    binMaxLoad: props.binMaxLoad ?? 0,
    binRecommendedLoad: props.binRecommendedLoad ?? 0,
    licensePlate: props.licensePlate ?? '',
    wardId: props.wardId ?? '',
    binCategory: props.binCategory ?? '',
    binClearedTime: props.binClearedTime ?? '',
    binFillingLevel: props.binFillingLevel ?? '0',
    location: props.location ?? null,
    binAddress: props.binAddress,
    binName: props.binName,
    typeWaste: props.typeWaste,
    icon: garbageIcon,
  };

  const geometry = {
    coordinates: apiObj?.geometry?.coordinates,
    type: apiObj?.geometry?.type,
  };
  return new GarbageModel(propsGarbage, geometry);
};

export const aforoFromAPIMapper = (apiObj: any): AforoModel => {
  if (!apiObj || !apiObj.properties || !apiObj.geometry) {
    throw new Error('Invalid API object format');
  }

  const { id, address, name, cameraType, status, description, nlId, speed, speedAverage, count, vehicleCount, motorcycleCount, pedestrianCount } = apiObj.properties;

  const geometry = {
    coordinates: apiObj.geometry.coordinates,
    type: apiObj.geometry.type,
  };

  const propsAforo: IPropsAforoModel = {
    id,
    address,
    title: name,
    name, cameraType, status, description,
    markerPopupText: address,
    nlId,
    icon: status === 'ACTIVO' ? Icon.camera_map_green :
      status === 'INACTIVO' ? Icon.camera_map_red :
        Icon.camera_map_blue,
    //Data
    speed: speed || speedAverage,
    vehicleCount,
    motorcycleCount,
    pedestrianCount,
    count,
  };

  return new AforoModel(propsAforo, geometry);
};

export const matrixODcameraFromAPIMapper = (apiObj: any): MatrixODCameraModel => {
  const propsCameraOD: IPropsMatrixCameraODModel = {
    id: apiObj?.properties.id,
    address: apiObj?.properties.name,
    url: apiObj?.properties.url,
    name: apiObj?.properties.name,
    cameraType: apiObj?.properties.name,
    status: apiObj?.properties.status,
    description: apiObj?.properties.name,
    markerPopupText: apiObj?.properties.name,
    title: apiObj?.properties.name,
    icon: Icon.camera_OD_map,
    cameraNlId: apiObj?.properties.nlId,
    coordinates: [apiObj?.geometry?.coordinates[0], apiObj?.geometry?.coordinates[1]]
  };

  const geometry = {
    coordinates: [apiObj?.geometry?.coordinates[0], apiObj?.geometry?.coordinates[1]],
    type: apiObj?.geometry?.type
  };
  return new MatrixODCameraModel(propsCameraOD, geometry);

};
export const matrixODFromAPIMapper = (apiObj: any): MatrixODModel => {
  const propsSensorOD: IPropsMatrixODModel = {
    id: apiObj?.properties.id,
    address: apiObj?.properties.name,
    title: apiObj?.properties.name,
    name: apiObj?.properties.name,
    cameraType: apiObj?.properties.name,
    status: apiObj?.properties.state,
    description: apiObj?.properties.name,
    markerPopupText: apiObj?.properties.name,
    icon: Icon.sensor_OD,
    coordinates: [apiObj?.geometry?.coordinates[1], apiObj?.geometry?.coordinates[0]]
  };

  const geometry = {
    coordinates: [apiObj?.geometry?.coordinates[1], apiObj?.geometry?.coordinates[0]],
    type: apiObj?.geometry?.type
  };

  return new MatrixODModel(propsSensorOD, geometry);

};

export const PanicButtonFromApiMapper = (apiObj: any): PanicButtonModel => {
  const { properties } = apiObj ?? {};

  const propsPanicButton: IPropsPanicButtonModel = {
    ...properties,
    icon: Icon.panic_button,
    id: properties?.id,
    establishment:properties?.establishment,
    title: `${properties?.id ?? ''} ${properties?.establishment ?? ''}`.trim(),
    adress: properties?.adress,
    phone: properties?.phone,
    registerDate: properties?.registerDate,
    creationDate: properties?.creationDate,
    markerPopupText: properties?.establishment,
  };

  const geometry = {
    coordinates: apiObj?.geometry?.coordinates,
    type: apiObj?.geometry?.type ?? 'Point',
  };

  return new PanicButtonModel(propsPanicButton, geometry);
};

export const situationsFromAPIMapper = (apiObj: any): SituationsModel => {

  const propsSituations: IPropsSituationsModel = {
    id: apiObj?.properties?.id,
    location: apiObj?.properties?.location,
    situationType: apiObj?.properties?.situationId,
    situationName: apiObj?.properties?.situationName,
    creationDate: apiObj?.properties?.creationDate,
    eventDate: apiObj?.properties?.beginDate,
    status: apiObj?.properties?.stateName,
    category: apiObj?.properties?.categoryName,
    subcategory: apiObj?.properties?.subcategoryName,
    priority: apiObj?.properties?.priorityName,
    postalcode: apiObj?.properties?.postalCode,
    mediaName: apiObj?.properties?.mediaName,
    source: apiObj?.properties?.sourceName,
    title: `ID ${apiObj?.properties?.id} - ${apiObj?.properties?.location}`,
    markerPopupText: `ID ${apiObj?.properties?.id} - ${apiObj?.properties?.location}`,
    observationsAuthor: apiObj.properties.observations[apiObj.properties.observations.length - 1].authorName,
    observations: apiObj.properties.observations[apiObj.properties.observations.length - 1].observation,
    icon: iconClassSituationsMap[`${apiObj?.properties?.situationName} ${apiObj?.properties?.stateName}` as string],
  };

  const geometry = {
    coordinates: apiObj?.geometry?.coordinates,
    type: apiObj?.geometry?.type,
  };

  return new SituationsModel(propsSituations, geometry);
};

export const trafficFromAPIMapper = (apiObj: any): TrafficModel => {
  const propsTraffic: IPropsTrafficModel = {
    id: apiObj?.properties?.id,
    nameFrom: apiObj?.properties?.nameFrom,
    nameTo: apiObj?.properties?.nameTo,
    distance: apiObj?.properties?.distance,
    levelOfService: apiObj?.properties?.levelOfService,
    concordance: apiObj?.properties?.concordanceIndex,
    speed: apiObj?.properties?.speed,
    elapsedTime: apiObj?.properties?.elapsedTime,
    expectedValue: apiObj?.properties?.expectedValue,
    readings: apiObj?.properties?.readings,
    readTime: apiObj?.properties?.readTime,
    time: apiObj?.properties?.time,
    speedRange: apiObj?.speedRange,
    geometry: apiObj?.geometry,
    typeVector: apiObj?.vectorTypeName,
    apiObj,
  };

  const geometry = {
    coordinates: apiObj?.geometry?.coordinates,
    type: apiObj?.geometry?.type,
  };

  return new TrafficModel(propsTraffic, geometry);

  // let model: TrafficModel;

  // if (apiObj?.geometry?.type === GeometryTypeEnum.Point) {
  //   const geometry = {
  //     type: apiObj?.geometry?.type,
  //     lat: apiObj?.geometry?.coordinates?.[1],
  //     lng: apiObj?.geometry?.coordinates?.[0],
  //   };
  //   model = new TrafficModel(propsTraffic, geometry);
  // } else {
  //   const geometry = {
  //     type: apiObj?.geometry?.type,
  //     coordinates: apiObj?.geometry?.coordinates,
  //   };
  //   const lat =
  //     geometry.type === GeometryTypeEnum.LineString
  //       ? geometry.coordinates[0][1]
  //       : geometry.coordinates[0][0][1];
  //   const lng =
  //     geometry.type === GeometryTypeEnum.LineString
  //       ? geometry.coordinates[0][0]
  //       : geometry.coordinates[0][0][0];

  //   model = new TrafficModel(propsTraffic, geometry, lat, lng);
  // }
};

export const cameraFromAPIMapper = (apiObj: any): CameraModel => {
  const propsCamera: IPropsCameraModel = {
    id: apiObj?.properties?.id,
    icon: Icon.layer_cameras,
    address: apiObj?.properties?.address,
    name: apiObj?.properties?.name,
    markerPopupText: `${apiObj?.properties?.name}`,
    idNeural: apiObj?.properties?.nlId,
  };

  const geometry = {
    coordinates: apiObj?.geometry?.coordinates,
    type: apiObj?.geometry?.type,
  };

  return new CameraModel(propsCamera, geometry);
};

export const trafficSpeedRangeFromAPIMapper = (apiObj: any): TrafficSpeedRangeModel => {
  const { properties, geometry } = apiObj;

  const {
    nameFrom, nameTo,
    // id, distance, levelOfService, speed, elapsedTime, expectedValue, readings, readTime, time,
    vectorTypeName, concordanceIndex, rangeTypeColor, rangeTypeName
  } = properties;

  const propsTrafficSpeedRange: IPropsTrafficSpeedRangeModel = {
    ...properties,
    concordance: concordanceIndex,
    typeVector: vectorTypeName,
    typeColor: rangeTypeColor ?? '#FFFFFF',
    rangeTypeName,
    icon: Icon.concordance_green,
    markerPopupText: `<div style="display:flex; flex-direction:column; justify-content:center;">
      <span style="text-align:center;">${vectorTypeName}</span>
      <span style="text-align:center;">${nameFrom} - ${nameTo}</span>
    </div>`
  };

  return new TrafficSpeedRangeModel(propsTrafficSpeedRange, geometry);
};


export const trafficConcordanceFromAPIMapper = (
  apiObj: any
): TrafficConcordanceModel => {
  const props = apiObj?.properties;
  const propsTrafficConcordance: IPropsTrafficConcordanceModel = {
    id: props?.id,
    nameFrom: props?.nameFrom,
    nameTo: props?.nameTo,
    distance: props?.distance,
    levelOfService: props?.levelOfService,
    concordance: props?.concordanceIndex,
    speed: props?.speed,
    elapsedTime: props?.elapsedTime,
    expectedValue: props?.expectedValue,
    readings: props?.readings,
    readTime: props?.readTime,
    time: props?.time,
    typeVector: props?.vectorTypeName,
    typeColor: props?.rangeTypeColor,
    rangeTypeName: props?.rangeTypeName,
    icon: Icon.map_traffic_light,
    markerPopupText: `<div style="display:flex; flex-direction:column; justify-content:center;">
      <span style="text-align:center;">${props?.vectorTypeName}</span>
      <span style="text-align:center;">${props?.nameFrom} - ${props?.nameTo}</span>
    </div>`
  };

  const geometry = {
    coordinates: apiObj?.geometry?.coordinates,
    type: apiObj?.geometry?.type,
  };
  return new TrafficSpeedRangeModel(propsTrafficConcordance, geometry);
};

export const camerapointsFromAPIMapper = (apiObj: any): CameraPointsModel => {
  const propsCamera: IPropsCameraPointModel = {
    id: apiObj?.properties?.id,
    icon: Icon.camera_lpr_map,
    address: apiObj?.properties?.address,
    url: apiObj?.properties?.url,
    cameraType: apiObj?.properties?.cameraType,
    name: apiObj?.properties?.name,
    status: apiObj?.properties?.status,
    description: apiObj?.properties?.description,
    markerPopupText: `${apiObj?.properties?.id}, ${apiObj?.properties?.name}`,
  };

  const geometry = {
    coordinates: apiObj?.geometry?.coordinates,
    type: apiObj?.geometry?.type,
  };

  return new CameraPointsModel(propsCamera, geometry);
};

export const bicyclePathFromAPIMapper = (apiObj: any): BicyclePathModel => {
  const propsBicycle: IPropsBicyclePathModel = {
    id: apiObj?.properties?.id,
    type: apiObj?.properties?.TIPO,
    distance: apiObj?.properties?.DISTANCIA,
    width: apiObj?.properties?.ANCHO,
    shapeLength: apiObj?.properties?.Shape_Leng,
    editor: apiObj?.properties?.EDITOR,
    date: apiObj?.properties?.FECHA,
    status: apiObj?.properties?.ESTADO,
    speed: apiObj?.properties?.VELOCIDAD,
    slope: apiObj?.properties?.PENDIENTE,
    title: `${apiObj?.properties?.layer}`,
    markerPopupText: `${apiObj?.properties?.layer} ${apiObj?.properties?.refname}`,
    icon: Icon.layer_bicycle
  };

  const geometry = {
    coordinates: apiObj?.geometry?.coordinates,
    type: apiObj?.geometry?.type,
  };

  return new BicyclePathModel(propsBicycle, geometry);
};

export const parkingLotFromAPIMapper = (apiObj: any): ParkingLotModel => {
  const propsBicycle: IPropsParkingLotModel = {
    id: apiObj?.properties?.id,
    location: apiObj?.properties?.DIRECCION,
    carCapacity: apiObj?.properties?.CAPACI_AUT,
    motorcycleCapacity: apiObj?.properties?.CAPACI_MOT,
    bicycleCapacity: apiObj?.properties?.CAPACI_BIC,
    busCapacity: apiObj?.properties?.CAPACI_BUS,
    startHourWeek: apiObj?.properties?.HOR_I_SEM,
    endHourWeek: apiObj?.properties?.HOR_F_SEM,
    startHourSaturday: apiObj?.properties?.HOR_I_SAB,
    endHourSaturday: apiObj?.properties?.HOR_F_SAB,
    startHourSunday: apiObj?.properties?.HOR_I_DOM,
    endHourSunday: apiObj?.properties?.HOR_F_DOM,
    operator: apiObj?.properties?.OPERADOR,
    title: apiObj?.properties?.DIRECCION,
    markerPopupText: apiObj?.properties?.DIRECCION,
    icon: Icon.map_parking,
  };

  const geometry = {
    coordinates: apiObj?.geometry?.coordinates,
    type: apiObj?.geometry?.type,
  };

  return new ParkingLotModel(propsBicycle, geometry);
};

export const trafficLightFromAPIMapper = (apiObj: any): TrafficLightModel => {
  const propsTrafficLight: IPropsTrafficLightModel = {
    id: apiObj?.properties?.Id,
    access: apiObj?.properties?.ACCESO,
    pole: apiObj?.properties?.TIPO_POSTE,
    lightbulbType: apiObj?.properties?.TIP_BOM,
    operation: apiObj?.properties?.OPERACION,
    side: apiObj?.properties?.COSTADO,
    active: apiObj?.properties?.ACTIVO,
    address: apiObj?.properties?.DIRECCIO_1,
    sound: apiObj?.properties?.SONORIZACI,
    title: apiObj?.properties?.DIRECCIO_1,
    markerPopupText: apiObj?.properties?.DIRECCIO_1,
    icon: Icon.map_traffic_light,
  };

  const geometry = {
    coordinates: apiObj?.geometry?.coordinates,
    type: apiObj?.geometry?.type,
  };

  return new TrafficLightModel(propsTrafficLight, geometry);
};

export const agentsFromAPIMapper = (apiObj: any): AgentsModel => {

  const propsAgents: IPropsAgentsModel = {
    id: String(apiObj?.properties?.id),
    name: apiObj?.properties?.name,
    agentType:
      apiObj?.properties?.agentType.toUpperCase() === 'AGENTE DE TRÁNSITO'
        ? 'transit'
        : 'police',
    updatedDate: apiObj?.properties?.updatedDate,
    title: `AGENTE ID ${apiObj?.properties?.id}`,
    markerPopupText: `AGENTE ID ${apiObj?.properties?.id}`,
    icon:
      apiObj?.properties?.agentType.toUpperCase() === 'AGENTE DE TRÁNSITO'
        ? Icon.map_agents_transit
        : Icon.map_agents_police,
  };

  const geometry = {
    coordinates: apiObj?.geometry?.coordinates,
    type: apiObj?.geometry?.type,
  };

  return new AgentsModel(propsAgents, geometry);
};

export const trafficManagmentPlansFromAPIMapper = (
  apiObj: any
): TrafficManagmentPlansPathModel => {
  const propsTrafficManagment: IPropsTrafficManagmentPathModel = {
    id: apiObj?.properties?.objectid,
    globalid: apiObj?.properties?.globalid,
    objectid: apiObj?.properties?.OBJECTID_1,
    contratista: apiObj?.properties?.contratist,
    direccion: apiObj?.properties?.direccion,
    empresacon: apiObj?.properties?.empresacon,
    fechaampli: apiObj?.properties?.fechaampli,
    fechaaprob: apiObj?.properties?.fechaaprob,
    fechafin: apiObj?.properties?.fechafin,
    fechainici: apiObj?.properties?.fechainici_1,
    fechasegui: apiObj?.properties?.fechasegui,
    interven: apiObj?.properties?.interven_1,
    intervento: apiObj?.properties?.intervento,
    lastedite: apiObj?.properties?.lastedite,
    numerocelu: apiObj?.properties?.numerocelu,
    numeropmt: apiObj?.properties?.numeropmt,
    observacion: apiObj?.properties?.observacio,
    type: apiObj?.properties?.tipo_cierr,
    vigencia: apiObj?.properties?.vigencia,
    apiObj,
    geometry: apiObj?.geometry,
    title: `ID ${apiObj?.properties?.objectid}`,
    markerPopupText: `ID ${apiObj?.properties?.objectid}`,
    icon: Icon.map_pmt,
  };

  const geometry = {
    coordinates: apiObj?.geometry?.coordinates,
    type: apiObj?.geometry?.type,
  };

  return new TrafficManagmentPlansPathModel(propsTrafficManagment, geometry);
};

export const StopBusFromAPIMapper = (apiObj: any): StopBusModel => {
  const propsStopBus: IPropsStopBusModel = {
    id: apiObj?.id.split(".")[1],
    name: apiObj?.properties?.refname,
    date: apiObj?.properties?.docupdate,
    rutas: apiObj?.properties?.rutas.split('-'),
    markerPopupText: `Parada  ${apiObj?.id.split(".")[1]}`,
    icon: Icon.layer_stop_bus,
  };

  const geometry = {
    coordinates: apiObj?.geometry?.coordinates,
    type: apiObj?.geometry?.type,
  };

  return new StopBusModel(propsStopBus, geometry);
};

export const RouteBusFromAPIMapper = (apiObj: any): RouteBusModel => {
  const propsStopBus: IPropsRouteBusModel = {
    id: apiObj?.properties?.refname,
    name: apiObj?.properties?.refname,
    date: apiObj?.properties?.docupdate,
    rutas: apiObj?.properties?.rutas,
    //markerPopupText: `Ruta   ${apiObj?.properties?.refname}`,
    color: apiObj?.properties?.color,
    rgb: apiObj?.properties?.rgb,
    markerPopupText: `<div style="display:flex; flex-direction:column; justify-content:center;">
      <span style="text-align:center;">${apiObj?.properties?.refname}</span>
    </div>`
  };

  const geometry = {
    coordinates: apiObj?.geometry?.coordinates,
    type: apiObj?.geometry?.type,
  };

  return new RouteBusModel(propsStopBus, geometry);
};

export const agentsHistoricalFromAPIMapper = (apiObj: any): AgentsModel => {
  const propsAgentsHistorical: IPropsAgentsHistoricalModel = {
    id: apiObj?.properties?.id,
    name: apiObj?.properties?.name,
    agentType: apiObj?.properties?.agentType,
    updatedDate: apiObj?.properties?.updatedDate,
    time: apiObj?.properties?.time,
    title: apiObj?.properties?.time,
    markerPopupText: apiObj?.properties?.time,
    icon: Icon.dot,
  };

  const geometry = {
    coordinates: apiObj?.geometry?.coordinates,
    type: apiObj?.geometry?.type,
  };

  return new AgentsHistoricalModel(propsAgentsHistorical, geometry);
};

export const ZonesFromAPIMapper = (apiObj: any): ZonesPathModel => {
  const propsZones: IPropsZonesPathModel = {
    id: apiObj?.properties?.id,
    ancho: apiObj?.properties?.ANCHO,
    capacidad: apiObj?.properties?.CAPACIDAD,
    direccion: apiObj?.properties?.DIRECCION,
    editor: apiObj?.properties?.EDITOR,
    estado: apiObj?.properties?.ESTADO,
    fechaEstado: apiObj?.properties?.FECH_ESTAD,
    horaFin: apiObj?.properties?.HOR_FIN,
    horaInicio: apiObj?.properties?.HOR_INIC,
    largo: apiObj?.properties?.LARGO,
    objectId: apiObj?.properties?.OBJECTID,
    reglamento: apiObj?.properties?.REGLAMENT,
    tarifa: apiObj?.properties?.TARIFA,
    tarifaAuto: apiObj?.properties?.TARIFA_AUT,
    tarifaMoto: apiObj?.properties?.TARIFA_MOT,
    tipo: apiObj?.properties?.TIPO,
    apiObj,
    geometry: apiObj?.geometry,
    title: `ID ${apiObj?.properties?.DIRECCION}`,
    markerPopupText: `ID ${apiObj?.properties?.DIRECCION}`,
    icon:
      apiObj?.properties?.TIPO === 'CARGUE Y DESCARGUE'
        ? Icon.map_zones_white
        : apiObj?.properties?.TIPO === 'ZONA AMARILLA'
          ? Icon.map_zones_yellow
          : apiObj?.properties?.TIPO === 'ZONA NARANJA'
            ? Icon.map_zones_orange : Icon.map_zones_blue,
  };

  const geometry = {
    coordinates: apiObj?.geometry?.coordinates,
    type: apiObj?.geometry?.type,
  };

  return new ZonesPathModel(propsZones, geometry);
};

export const RoadRunFromAPIMapper = (apiObj: any): RoadRunPathModel => {
  const propsRoadRun: IPropsRoadRunPathModel = {
    id: apiObj?.properties?.ID,
    nombreVia: apiObj?.properties?.NOMVIAL,
    ancho: apiObj?.properties?.UN_ANCHO_C,
    capacidad: apiObj?.properties?.CAPACIDAD_,
    largo: apiObj?.properties?.LENGTH,
    tipo: apiObj?.properties?.TIPOVIAPOT,
    sentido: apiObj?.properties?.DIR,
    velocidad: apiObj?.properties?.VELOCIDAD1,
    title: `VIA ${apiObj?.properties?.NOMVIAL}`,
    markerPopupText: `VIA ${apiObj?.properties?.NOMVIAL}`,
  };

  const geometry = {
    coordinates: apiObj?.geometry?.coordinates,
    type: apiObj?.geometry?.type,
  };

  return new RoadRunPathModel(propsRoadRun, geometry);
};

export const accidentFromApiMapper = (apiObj: any): AccidentModel => {
  const propsAccident: IPropsAccidentModel[] = apiObj.properties;

  const geometry = {
    coordinates: apiObj?.geometry?.coordinates,
    type: apiObj?.geometry?.type,
  };

  return new AccidentModel(propsAccident, geometry);
};

export const UndergroundParkingSpotFromApiMapper = (apiObj: any): UndergroundParkingSpotModel => {
  const {
    availableSpotNumber, occupiedSpotNumber, occupiedSpotPercentage,
    id, type, name, totalSpotNumber,
  } = apiObj?.properties || {};
  const { coordinates, type: geometryType } = apiObj?.geometry || {};

  const icon = Icon.parking_state_underground;
  const propsParkingSpot: IPropsUndergroundParkingSpotModel = {
    id,
    type,
    availableSpotNumber,
    occupiedSpotNumber,
    occupiedSpotPercentage,
    markerPopupText: name,
    name,
    totalSpotNumber,
    icon,
  };

  const geometry = { coordinates, type: geometryType, };

  return new UndergroundParkingSpotModel(propsParkingSpot, geometry);
}


export const ParkingSpotFromApiMapper = (apiObj: any): ParkingSpotModel => {
  const props = apiObj?.properties || {};
  const { id, name, type, status, zone, refDevice, refParkingSite, modifiedAt, occupation } = props;
  const { coordinates, type: geometryType } = apiObj?.geometry || {};

  const safeStatus = status ?? 'unknown';
  const safeName = name ?? '';

  let icon;
  switch (safeStatus) {
    case 'free':
      icon = zone === 'forLoadUnload' ? Icon.parking_state_loading_free : Icon.parking_state_free;
      break;
    case 'occupied':
      icon = zone === 'forLoadUnload' ? Icon.parking_state_loading_occupied : Icon.parking_state_occupied;
      break;
    case 'closed':
    default:
      icon = zone === 'forLoadUnload' ? Icon.parking_state_loading_unknown : Icon.parking_state_unknown;
  }

  const propsParkingSpot: IPropsParkingSpotModel = {
    id: id ?? '',
    name: safeName,
    type: type,
    status: safeStatus,
    markerPopupText: safeName || (refDevice ? `Sensor ${refDevice}` : 'Plaza de parking'),
    icon,
    zone: zone != null ? String(zone) : undefined,
    refDevice,
    refParkingSite,
    modifiedAt,
    occupation: occupation != null ? Number(occupation) : undefined,
  };

  const geometry = { coordinates: coordinates ?? [], type: geometryType ?? 'Point' };

  return new ParkingSpotModel(propsParkingSpot, geometry);
};

export const ParkingSqareFromApiMapper = (apiObj: any): ParkingSpotModel => {
  const { id, name, type, status, zone } = apiObj?.properties || {};
  const { coordinates, type: geometryType } = apiObj?.geometry || {};
  let icon;

  if (name.includes('PMR')) {


    switch (status) {
      case 'free':
        icon = Icon.PMR_Disponible;
        break;
      case 'occupied':
        icon = Icon.PMR_Ocupado;
        break;
      case null:
        icon = Icon.PMR_Indefinido;
        break;
      default:
        icon = Icon.PMR_Indefinido;
    }
  } else {
    switch (status) {
      case 'free':
        icon = Icon.parking_state_free;
        break;
      case 'occupied':
        icon = Icon.parking_state_occupied;
        break;
      case null:
        icon = Icon.parking_state_occupied;
        break;
      default:
        icon = Icon.parking_state_unknown;
    }
  }
  const propsParkingSpot: IPropsParkingSpotModel = {
    id,
    name,
    type,
    status,
    markerPopupText: name,
    icon,
  };

  const geometry = { coordinates, type: geometryType, };

  return new ParkingSpotModel(propsParkingSpot, geometry);
};


export const ParkingLineFromApiMapper = (apiObj: any): ParkingLineModel => {

  const {
    id, name, type, category, availableSpotNumber, occupiedSpotNumber, totalSpotNumber, maximumParkingDuration
  } = apiObj.properties || {};

  const propsParkingLine: IPropsParkingLineModel = {
    id,
    name,
    type,
    category,
    availableSpotNumber,
    occupiedSpotNumber,
    totalSpotNumber,
    maximumParkingDuration,
    markerPopupText:
      `<div style="justify-content: center; align-items: center; display: grid; text-align: center">
              <div class="title">
                <span>
                ${category === ParkingLinesEnum.BLUE_ZONE ? 'Zona Azul'
        : category === ParkingLinesEnum.GREEN_ZONE ? 'Zona Verde' : ''}
                </span>
              </div>
              <div class="subtitle">
                <span> ${name} </span>
              </div>
          </div>`,
  };

  const geometry = {
    coordinates: apiObj?.geometry?.coordinates,
    type: apiObj?.geometry?.type,
  };

  return new ParkingLineModel(propsParkingLine, geometry);
};

export const ParkingLoadingZoneFromApiMapper = (apiObj: any): ParkingLoadingZoneModel => {
  const propsParkingLoadingZone: IPropsParkingLoadingZoneModel = {
    id: apiObj?.properties?.id,
    name: apiObj?.properties?.name,
    popupinfo: apiObj?.properties?.popupinfo,
    type: 'ParkingLoadingZone',
    // icon: Icon.AirQuality_B
  };

  const geometry = {
    coordinates: apiObj?.geometry?.coordinates,
    type: apiObj?.geometry?.type,
  };

  return new ParkingLoadingZoneModel(propsParkingLoadingZone, geometry);
};

export const pmvAPIMapper = ({ properties = {}, geometry = {} }: any): PmvModel => {
  const {
    id, title, description,
    gatewat,
    icon,
    ip,
    markerPopupText,
    name,
    panelType,
    port,
    protocol,
    stateId,
    stateName,
    password,
    serial,
    address,
  } = properties as IPmvModel;

  const propsAgents: IPmvModel = {
    ...properties,
    title: properties.name,
    markerPopupText: `${address}`,
    icon: Icon.map_pmv,
  };

  const geo = {
    coordinates: geometry?.coordinates,
    type: geometry?.type,
  };

  return new PmvModel(propsAgents, geo);
};

export const runtFromAPIMapper = (apiObj: any): RuntDatexModel => {
  const propsRuntDatex: IPropsRuntDatexModel = {
    id: apiObj?.properties?.id,
    address: apiObj?.properties?.address,
    icon: Icon.layer_runt,
    title: apiObj?.properties?.address,
    markerPopupText: apiObj?.properties?.address,
  };

  const geometry = {
    coordinates: apiObj?.geometry?.coordinates,
    type: apiObj?.geometry?.type,
  };

  return new RuntDatexModel(propsRuntDatex, geometry);
};

export const AirQualityPointsAPIMapper = (apiObj: any): AirQualityModel => {
  const propsAirQuality: IPropsAirQualityModel = {
    id: apiObj?.properties?.id,
    name: apiObj?.properties?.name,
    location: apiObj?.properties?.location,
    temperature: apiObj?.properties?.temperature,
    stationType: apiObj?.properties?.stationType,
    humidity: apiObj?.properties?.humidity,
    pressure: apiObj?.properties?.pressure,
    icon: (apiObj?.properties?.stationType == 'Mini Estación') ? Icon.MiniStationMap : Icon.StationIconMapGreen,
    title: apiObj?.properties?.location,
    lastModification: apiObj?.properties?.temperature.fecha_lectura.value,
    type: apiObj?.properties?.type,
    postal_code: apiObj?.properties?.postal_code,
    carbonDioxide: apiObj?.properties?.carbonDioxide,
    psi: apiObj?.properties?.psi,
    pm10: apiObj?.properties?.pm10,
    pm25: apiObj?.properties?.pm25,
    no2: apiObj?.properties?.no2,
    o3: apiObj?.properties?.o3,
    so2: apiObj?.properties?.so2,

    markerPopupText:
      `
          <div style="justify-content: center; align-items: center; display: grid; text-align: center">
              <div class="title">
                <span>
                ${apiObj?.properties?.location}
                </span>
              </div>              
          </div>
        `,
  };

  const geometry = {
    coordinates: apiObj?.geometry?.coordinates,
    type: apiObj?.geometry?.type,
  };
  return new AirQualityModel(propsAirQuality, geometry);
};

export const skateboardFromAPIMapper = (apiObj: any): SkateboardModel => {
  const propsSkateboard: IPropsSkateboardModel = {
    id: apiObj?.properties?.id,
    icon: Icon.layer_scooter,
    address: apiObj?.properties?.address,
    name: apiObj?.properties?.layer,
    markerPopupText: `${apiObj?.properties?.refname}`,
  };

  const geometry = {
    coordinates: apiObj?.geometry?.coordinates,
    type: apiObj?.geometry?.type,
  };

  return new SkateboardModel(propsSkateboard, geometry);
};

